import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ComplianceSelectedPolicies {
  position: number;
  name: string;
}
export interface ComplianceSection {
  index: number;
  title: string;
  selectedPolicies: ComplianceSelectedPolicies[];
}

export interface ComplianceMetadata {
  name: string;
  description: string;
}
export interface ComplianceData {
  metaData: ComplianceMetadata;
  sections: ComplianceSection[];
}

export interface ComplianceState extends ComplianceData {
  activeStep: number;
}

const initialState: ComplianceState = {
  activeStep: 0,
  metaData: { name: '', description: '' },
  sections: [],
};
const ComplianceSlice = createSlice({
  name: 'Compliance',
  initialState,
  reducers: {
    resetComplianceData: () => initialState,
    setComplianceStep: (
      state: ComplianceState,
      action: PayloadAction<number>,
    ) => {
      state.activeStep = action.payload;
    },
    setComplianceMetaData: (
      state: ComplianceState,
      action: PayloadAction<ComplianceMetadata>,
    ) => {
      state.metaData = action.payload;
    },
    setComplianceSectionData: (
      state: ComplianceState,
      action: PayloadAction<ComplianceSection[]>,
    ) => {
      state.sections = action.payload;
    },
    setComplianceData: (
      state: ComplianceState,
      action: PayloadAction<ComplianceData>,
    ) => {
      const { metaData, sections } = action.payload;
      state.metaData = metaData;
      state.sections = sections;
    },
    addComplianceSection: (
      state: ComplianceState,
      action: PayloadAction<ComplianceSection>,
    ) => {
      state.sections.push(action.payload);
    },
    deleteComplianceSection: (
      state: ComplianceState,
      action: PayloadAction<number>,
    ) => {
      const index = action.payload;
      if (state.sections && index >= 0 && index < state.sections.length) {
        state.sections.splice(index, 1);
      }
    },
    editComplianceSection: (
      state: ComplianceState,
      action: PayloadAction<ComplianceSection>,
    ) => {
      const section = action.payload;
      state.sections[section.index] = section;
    },
    setCompliancePolicies: (
      state: ComplianceState,
      action: PayloadAction<{
        index: number;
        selectedPolicies: ComplianceSelectedPolicies[];
      }>,
    ) => {
      const { index, selectedPolicies } = action.payload;
      if (index > state.sections.length) return;
      let current = 0;
      const policies = selectedPolicies.map((row) => {
        const obj = { name: row.name, position: current };
        current++;
        return obj;
      });
      state.sections[index].selectedPolicies = policies;
    },
  },
});

export const {
  setComplianceStep,
  setComplianceMetaData,
  setComplianceSectionData,
  setComplianceData,
  addComplianceSection,
  deleteComplianceSection,
  editComplianceSection,
  setCompliancePolicies,
  resetComplianceData,
} = ComplianceSlice.actions;

export const complianceReducer = ComplianceSlice.reducer;
